/**
 * Central icon mapping for the app.
 *
 * Always import icons from here (not directly from `lucide-react`) when the
 * icon represents one of these app concepts. This guarantees the same glyph
 * is used everywhere a concept appears (nav, badges, empty states, CTAs…).
 *
 * If you need a one-off icon that isn't a recurring concept, importing from
 * `lucide-react` directly is fine.
 */
import {
  // Navigation / sections
  LayoutDashboard,
  ClipboardList,
  BookOpen,
  Library,
  Settings,
  Info,
  // Concept icons
  MessageCircleHeart,
  Gem,
  Stethoscope,
  Brain,
  HeartPulse,
  ShieldCheck,
  LineChart,
  FileText,
  Wand2,
  Bell,
  Lock,
  Users,
  Mail,
  Activity,
  ShieldAlert,
  RefreshCw,
  Clock,
  // Auth
  LogIn,
  LogOut,
  // Generic affordances
  ArrowRight,
  Check,
  Download,
  ExternalLink,
  Phone,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Send,
  Plus,
  Trash2,
  History,
  Eye,
  EyeOff,
  Menu,
  PanelLeft,
  ChevronLeft,
  Loader2,
  AlertCircle,
  X,
  Square,
  MessageSquare,
  Sun,
  Moon,
  Apple,
  Chrome,
} from "lucide-react";

/**
 * Concept → icon mapping.
 * Use these aliases everywhere instead of re-picking icons per screen.
 */
export const Icons = {
  // App sections (must match MobileTabBar / AppShell nav)
  dashboard: LayoutDashboard,
  screenings: ClipboardList,
  chat: MessageCircleHeart,
  journal: BookOpen,
  resources: Library,
  settings: Settings,
  info: Info,

  // Subscription / premium
  premium: Gem,
  locked: Lock,

  // Clinical / wellbeing concepts
  clinical: Stethoscope,
  brain: Brain,
  heart: HeartPulse,
  shield: ShieldCheck,
  insights: LineChart,
  report: FileText,
  reminder: Bell,
  assist: Wand2,

  // Auth
  signIn: LogIn,
  signOut: LogOut,
  apple: Apple,
  google: Chrome,

  // Generic UI
  next: ArrowRight,
  back: ChevronLeft,
  check: Check,
  download: Download,
  externalLink: ExternalLink,
  phone: Phone,
  send: Send,
  add: Plus,
  delete: Trash2,
  history: History,
  show: Eye,
  hide: EyeOff,
  menu: Menu,
  panel: PanelLeft,
  loading: Loader2,
  alert: AlertCircle,
  close: X,
  stop: Square,
  thread: MessageSquare,
  micOn: Mic,
  micOff: MicOff,
  soundOn: Volume2,
  soundOff: VolumeX,
  themeLight: Sun,
  themeDark: Moon,
  users: Users,
  mail: Mail,
  activity: Activity,
  shieldAlert: ShieldAlert,
  refresh: RefreshCw,
  clock: Clock,
} as const;

export type IconName = keyof typeof Icons;
